/**
 * Client-side JSON Schema validation for instant feedback.
 *
 * This is UX, not security. The backend re-validates the same schema with the
 * same semantics, and that is the check that counts — a caller bypassing this
 * one gains nothing.
 */

import Ajv, { type ErrorObject, type ValidateFunction } from 'ajv'
import addFormats from 'ajv-formats'
import type { JsonSchema } from '@/core/types/api.types'

const ajv = new Ajv({
  allErrors: true,
  strict: false, // manifests carry x-ui / x-lookup, which strict mode rejects
  coerceTypes: false,
  useDefaults: false,
})
addFormats(ajv)

// Compilation is the expensive half; one validator per action is plenty.
const cache = new Map<string, ValidateFunction>()

function compile(cacheKey: string, schema: JsonSchema): ValidateFunction | null {
  const hit = cache.get(cacheKey)
  if (hit) return hit
  try {
    const fn = ajv.compile(schema as object)
    cache.set(cacheKey, fn)
    return fn
  } catch (err) {
    // A schema ajv refuses shouldn't block the form — the server still validates.
    console.warn(`[validation] could not compile schema for ${cacheKey}`, err)
    return null
  }
}

function pathOf(error: ErrorObject): string {
  if (error.keyword === 'required') {
    const missing = (error.params as { missingProperty?: string }).missingProperty
    const prefix = error.instancePath.replace(/^\//, '').replace(/\//g, '.')
    return prefix ? `${prefix}.${missing}` : (missing ?? '')
  }
  return error.instancePath.replace(/^\//, '').replace(/\//g, '.')
}

function humanize(error: ErrorObject): string {
  const params = error.params as Record<string, unknown>
  switch (error.keyword) {
    case 'required':
      return 'This field is required.'
    case 'minLength':
      return `Must be at least ${params.limit} characters.`
    case 'maxLength':
      return `Must be at most ${params.limit} characters.`
    case 'minimum':
      return `Must be ${params.limit} or more.`
    case 'maximum':
      return `Must be ${params.limit} or less.`
    case 'pattern':
      return `Does not match the expected format.`
    case 'enum':
      return `Must be one of: ${(params.allowedValues as unknown[])?.join(', ')}`
    case 'type':
      return `Must be a ${params.type}.`
    default:
      return error.message ?? 'Invalid value.'
  }
}

export interface ValidationResult {
  valid: boolean
  errors: Record<string, string>
}

export function validate(
  cacheKey: string,
  schema: JsonSchema,
  value: unknown,
): ValidationResult {
  const fn = compile(cacheKey, schema)
  if (!fn) return { valid: true, errors: {} }

  if (fn(value)) return { valid: true, errors: {} }

  const errors: Record<string, string> = {}
  for (const err of fn.errors ?? []) {
    const path = pathOf(err)
    // First error per field only: a stack of messages on one input is noise.
    if (path && !errors[path]) errors[path] = humanize(err)
  }
  return { valid: false, errors }
}
